// The tuning chat's card for a revised standard (lib/standardTune.js): the
// change marked in the wording, every finding it was checked on with what the
// current version and the revision answer, grouped by what moved, and saving
// the revision as the standard's next version.
import { useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { MORE_CHECK, getSuggestContext, moreLabel, nextToCheck, subscribeSuggestContext } from "../lib/standardSuggest.js";
import { checkRevision, standardById, wordDiff } from "../lib/standardTune.js";
import { CARD, CARD_BUTTON as BUTTON, CARD_MUTED as MUTED, CARD_PICKED as PICKED, ChatReplyContext, useKept } from "./ChatQuestionCard.jsx";
import { FILE_LABEL } from "./lenses/triageParts.jsx";

const useSuggestContext = () => useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);

// The groups in the order a reader needs them: what it fixed, then what to
// look at (a wrong flag it still makes, a real one it lost, one it newly
// makes), then what it kept.
const GROUPS = [
    ["fixed", "Fixed", "text-[color:var(--success,#2f7d4f)]"],
    ["caught", "Caught", "text-[color:var(--success,#2f7d4f)]"],
    ["still", "Still", "text-[color:var(--danger,#c0392b)]"],
    ["missed", "Missed", "text-[color:var(--danger,#c0392b)]"],
    ["lost", "Lost", "text-[color:var(--danger,#c0392b)]"],
    ["new", "New", "text-[color:var(--warn,#b7791f)]"],
    ["kept", "Kept", "text-[color:var(--muted)]"],
];
const ANSWER = {
    met: ["met", "border-[color:var(--danger,#c0392b)] text-[color:var(--danger,#c0392b)]"],
    unconfirmed: ["unsure", "border-[color:var(--line)] text-[color:var(--muted)]"],
    not_met: ["not met", "border-[color:var(--line)] text-[color:var(--muted)]"],
    error: ["couldn’t check", "border-dashed border-[color:var(--line)] text-[color:var(--muted-soft)]"],
};
const Answer = ({ value }) => {
    const [label, tone] = ANSWER[value] || ANSWER.not_met;
    return <span className={`inline-block rounded-[2px] border px-[5px] text-[10px] leading-[15px] ${tone}`}>{label}</span>;
};

// A long stretch the change did not touch reads as its ends, so the change is
// seen in its sentence without the whole standard around it.
const KEEP_EDGE = 10;
function Diff({ before, after, whole }) {
    const runs = wordDiff(before, after);
    return (
        <span data-revision-diff="" className="text-[12px] leading-[1.5] text-[color:var(--muted)] [overflow-wrap:anywhere]">
            {runs.map((r, i) => {
                if (r.op === "add") return <ins key={i} className="bg-[color:var(--control-bg)] text-[color:var(--ink)] no-underline [text-decoration:none] shadow-[inset_0_-1px_0_var(--ink)]">{r.text}</ins>;
                if (r.op === "cut") return <del key={i} className="text-[color:var(--danger,#c0392b)]">{r.text}</del>;
                const words = r.text.split(/(\s+)/);
                if (whole || words.length <= KEEP_EDGE * 4) return <span key={i}>{r.text}</span>;
                const head = i === 0 ? "" : words.slice(0, KEEP_EDGE * 2).join("");
                const tail = i === runs.length - 1 ? "" : words.slice(-KEEP_EDGE * 2).join("");
                return <span key={i}>{head}{"… "}{tail}</span>;
            })}
        </span>
    );
}

export function refineRevisionRequest({ name, text, rows, marked = [] }) {
    const still = rows.filter((r) => r.tag === "still");
    const lost = rows.filter((r) => r.tag === "lost");
    const unwanted = rows.filter((r) => marked.includes(r.id) && r.tag !== "still");
    const lines = [`Refine this revision of "${name}":`, text, ""];
    if (still.length) lines.push("It still flags these, and it shouldn't:", ...still.map((r) => `- ${r.title || "Untitled finding"} [id ${r.id}]`), "");
    if (unwanted.length) lines.push("It flags these too, and it shouldn't:", ...unwanted.map((r) => `- ${r.title || "Untitled finding"} [id ${r.id}]${r.why ? `: ${r.why}` : ""}`), "");
    const missed = rows.filter((r) => r.tag === "missed");
    if (missed.length) lines.push("It still doesn't flag these, and it should:", ...missed.map((r) => `- ${r.title || "Untitled finding"} [id ${r.id}]`), "");
    if (lost.length) lines.push("It no longer flags these, and it should:", ...lost.map((r) => `- ${r.title || "Untitled finding"} [id ${r.id}]`), "");
    lines.push("Suggest a few ways to refine it for me to pick from.");
    return lines.join("\n");
}

export function StandardRevisionCard({ nodeId, details }) {
    const ctx = useSuggestContext();
    const { reply, busy } = useContext(ChatReplyContext);
    const [state, setState] = useKept(nodeId, { extra: [], extraIds: [], saved: null, backfilled: false, refined: false, marked: [] });
    const [running, setRunning] = useState(null);
    const [same, setSame] = useState(false);
    const [whole, setWhole] = useState(false);
    const [backfill, setBackfill] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const controller = useRef(null);
    useEffect(() => () => controller.current?.abort(), []);

    const name = String(details?.name || "");
    const from = Number(details?.fromVersion) || 1;
    const newText = String(details?.newText || "");
    const rows = [...(details?.rows || []), ...state.extra];
    const checkedIds = [...(details?.checkedIds || []), ...state.extraIds];
    const total = Math.max(ctx?.ids?.length || 0, Number(details?.total) || 0, checkedIds.length);
    const by = (tag) => rows.filter((r) => r.tag === tag);
    const unchanged = by("same");
    const failed = by("error").length;
    const current = standardById(ctx, details?.standardId);
    const savedHere = state.saved || (current && String(current.text || "").trim() === newText.trim() ? current : null);
    const left = ctx ? nextToCheck(ctx.ids, checkedIds, MORE_CHECK).length : 0;

    const checkMore = async () => {
        if (!ctx || !current || running) return;
        const ids = nextToCheck(ctx.ids, checkedIds, MORE_CHECK);
        if (!ids.length) return;
        controller.current?.abort();
        const ctl = new AbortController();
        controller.current = ctl;
        setError("");
        setRunning({ done: 0, total: ids.length });
        try {
            let done = 0;
            const out = await checkRevision({
                ctx, item: { ...current, text: details.oldText, digest: current.version === from ? current.digest : null },
                text: newText, ids, signal: ctl.signal, onAnswer: () => { done += 1; setRunning({ done, total: ids.length }); },
            });
            if (!ctl.signal.aborted) setState((s) => ({ ...s, extra: [...s.extra, ...out.rows], extraIds: [...s.extraIds, ...ids] }));
        } catch (e) {
            if (!ctl.signal.aborted) setError(String(e?.message || e).slice(0, 200));
        } finally {
            setRunning(null);
        }
    };

    const refine = () => {
        if (!reply || busy || state.refined) return;
        setState((s) => ({ ...s, refined: true }));
        reply(refineRevisionRequest({ name, text: newText, rows, marked: state.marked }));
    };

    const save = async () => {
        if (!ctx?.canWrite || saving || !current) return;
        if (current.version !== from) {
            setError(`${name} changed since this was checked (it is v${current.version} now). Ask for the change again.`);
            return;
        }
        setSaving(true);
        setError("");
        const item = await ctx.revise(current, newText);
        if (item) {
            const check = backfill && ctx.runWindow ? await ctx.backfill(item, ctx.runWindow) : null;
            setState((s) => ({ ...s, saved: item, backfilled: Boolean(check?.run) }));
            if (check?.error) setError(`Saved, but older findings weren't checked: ${check.error}`);
        } else {
            setError("Couldn't save it. The Triage tab says why.");
        }
        setSaving(false);
    };

    // What the revision flags can be marked as wrong: Refine sends it.
    const markable = (r) => r.after === "met" && !["still", "fixed", "caught"].includes(r.tag);
    const toggleMark = (id) => setState((s) => ({ ...s, marked: (s.marked || []).includes(id) ? s.marked.filter((m) => m !== id) : [...(s.marked || []), id] }));
    const marked = state.marked || [];
    const Row = ({ r, tag, tone }) => {
        const on = marked.includes(r.id);
        return (
            <span data-revision-row={tag} data-revision-marked={on ? "" : undefined} className={`group/rev grid grid-cols-[46px_minmax(0,1fr)_64px_64px] items-baseline gap-x-[8px] border-t border-[color:var(--line-soft)] px-[4px] py-[5px] text-[12px] leading-[1.35] ${on ? "!bg-[color:var(--control-bg)]" : ""}`}>
                <span className={`font-mono text-[10px] tracking-[0.04em] uppercase ${tone}`}>{tag}</span>
                <span className="flex min-w-0 items-baseline gap-x-[6px]">
                    <span className={`min-w-0 truncate ${on ? "text-[color:var(--muted)] line-through" : "text-[color:var(--ink)]"}`} title={r.why || undefined}>{r.title || "Untitled finding"}</span>
                    {markable(r) && !savedHere && !state.refined ? (
                        <button type="button" data-revision-mark={r.id} aria-pressed={on} onClick={() => toggleMark(r.id)} className={`ml-auto shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[10px] ${on ? "text-[color:var(--ink)]" : "text-[color:var(--muted-soft)] opacity-0 group-hover/rev:opacity-100 focus-visible:opacity-100"}`}>
                            {on ? "\u2715 shouldn\u2019t" : "shouldn\u2019t match"}
                        </button>
                    ) : null}
                </span>
                <Answer value={r.before} />
                <Answer value={r.after} />
            </span>
        );
    };

    return (
        <div data-standard-revision="" className={CARD}>
            <span className="flex items-baseline gap-x-[8px]">
                <span className={FILE_LABEL}>{savedHere ? `Revision · ${name} · saved` : `Revision · ${name}`}</span>
                <span data-revision-count="" className="ml-auto font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                    v{from} {"→"} v{from + 1}{" · "}{checkedIds.length.toLocaleString()} of {total.toLocaleString()} checked
                </span>
            </span>
            <Diff before={details?.oldText} after={newText} whole={whole} />
            <span className="flex gap-x-[10px]">
                {details?.why ? <span className={`${MUTED} italic`}>{details.why}</span> : null}
                <button type="button" onClick={() => setWhole((v) => !v)} className="ml-auto shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">
                    {whole ? "Show the change" : "Show whole standard"}
                </button>
            </span>

            <span className="grid pt-[4px]">
                <span className={`grid grid-cols-[46px_minmax(0,1fr)_64px_64px] gap-x-[8px] px-[4px] pb-[2px] ${FILE_LABEL}`}>
                    <span />
                    <span>Finding</span>
                    <span>v{from} now</span>
                    <span>v{from + 1}</span>
                </span>
                {GROUPS.flatMap(([tag, label, tone]) => by(tag).map((r) => <Row key={`${tag}:${r.id}`} r={r} tag={label} tone={tone} />))}
                {unchanged.length ? (
                    <>
                        <button type="button" data-revision-same="" onClick={() => setSame((v) => !v)} className="flex cursor-pointer items-baseline gap-x-[8px] border-0 border-t border-solid border-[color:var(--line-soft)] bg-transparent px-[4px] py-[5px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                            <span className="w-[46px] font-mono text-[10px]">{"·"}</span>
                            {unchanged.length.toLocaleString()} not met either way {same ? "▾" : "▸"}
                        </button>
                        {same ? unchanged.map((r) => <Row key={`same:${r.id}`} r={r} tag="" tone="" />) : null}
                    </>
                ) : null}
            </span>
            <span data-revision-tally="" className="flex flex-wrap gap-x-[8px] border-t border-[color:var(--line-soft)] pt-[5px] text-[11px] text-[color:var(--muted)]">
                {GROUPS.map(([tag, label, tone]) => (by(tag).length ? <span key={tag} className={tone}>{by(tag).length} {label.toLowerCase()}</span> : null))}
                {failed ? <span>{failed} couldn&rsquo;t be checked</span> : null}
            </span>
            {error ? <span role="alert" className="text-[11px] leading-[1.4] text-[color:var(--danger,#c0392b)]">{error}</span> : null}

            <div className="flex flex-wrap items-center gap-[6px] border-t border-[color:var(--line-soft)] pt-[6px]">
                {running ? (
                    <>
                        <span className={MUTED}>Checking {running.done} of {running.total}{"…"}</span>
                        <button type="button" onClick={() => controller.current?.abort()} className={BUTTON}>Stop</button>
                    </>
                ) : (
                    <button type="button" data-revision-more="" onClick={checkMore} disabled={!ctx || !current || !left || Boolean(savedHere)} className={BUTTON}>
                        {moreLabel(left)}
                    </button>
                )}
                <span className="flex-1" />
                <button type="button" data-revision-refine="" onClick={refine} disabled={!reply || busy || state.refined || Boolean(savedHere)} className={BUTTON}>
                    {state.refined ? "Refine asked" : marked.length ? `Refine (${marked.length} marked)` : "Refine"}
                </button>
                {savedHere ? (
                    <span data-revision-saved="" className="text-[11px] text-[color:var(--ink)]">
                        Saved as v{savedHere.version}
                        {state.backfilled ? <span className="text-[color:var(--muted)]">; checking older findings</span> : null}
                    </span>
                ) : ctx?.canWrite ? (
                    <>
                        <label className="flex items-center gap-[4px] text-[11px] text-[color:var(--muted)]">
                            <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} data-revision-backfill="" />
                            check older
                        </label>
                        <button type="button" data-revision-save="" onClick={save} disabled={saving || !current} className={`${BUTTON} ${PICKED}`}>
                            {saving ? "Saving…" : `Save as v${from + 1}`}
                        </button>
                    </>
                ) : (
                    <span className={MUTED}>Saving needs permission to change settings.</span>
                )}
            </div>
        </div>
    );
}
